import React from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import {
  Stack,
  Typography,
  InputAdornment,
  Autocomplete,
  TextField,
  MenuItem,
  ListSubheader,
  Button,
} from '@mui/material'
import { useTheme } from '@mui/styles'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import Checkbox from 'components/generic/form/Checkbox'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import store from 'state/store'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import { AddCircle as AddCircleIcon } from '@mui/icons-material'
import { locationIsNotEmpty } from 'utils/helpers'

const PrintCheckBladeForm = ({
  payment,
  control,
  setValue,
  watch,
  errors,
  accountOptions,
  defaultMemo,
  customerData,
  automaticallyIncrementCheckNumber,
  checkNumber,
  supplierContactOptions,
  supplierLocationOptions,
  supplierContact,
  supplierLocation,
  updateContact,
  updateLocation,
  pricePrepend,
  isBatchBill,
}) => {
  const theme = useTheme()
  const useAutoincrementedCheckNumber = automaticallyIncrementCheckNumber
    ? watch(`${payment.id}.details.useAutoincrementedCheckNumber`)
    : false

  return (
    <Stack pl={3} pr={3} spacing={3}>
      <Typography variant="h5" gutterBottom>
        Check Details
      </Typography>
      <Stack spacing={3}>
        <Select
          data-testid="pay-from-select"
          name={`${payment.id}.details.payFrom`}
          label="Pay From"
          size="small"
          control={control}
          error={!!errors?.[payment.id]?.details?.payFrom}
          helperText={errors?.[payment.id]?.details?.payFrom?.message}
          defaultValue={accountOptions[0]?.id || ''}
          items={accountOptions}
          sx={{ width: '50%' }}
        />
        <Input
          data-testid="check-amount-input"
          name={`${payment.id}.details.checkAmount`}
          label="Check Amount"
          size="small"
          variant="outlined"
          type="number"
          pattern="[0-9]*"
          fractionDigits={2}
          control={control}
          disabled
          defaultValue={payment?.total || 0}
          InputProps={{
            startAdornment: (
              <InputAdornment sx={{ '*': { color: theme.palette.text.disabled } }} position="start">
                {pricePrepend}
              </InputAdornment>
            ),
            inputProps: {
              step: 0.01,
              min: 0,
            },
          }}
          sx={{ width: '50%' }}
        />
        <Stack direction="row" spacing={2}>
          <Input
            data-testid="check-number-input"
            name={`${payment.id}.details.checkNumber`}
            label="Check Number"
            size="small"
            variant="outlined"
            defaultValue={automaticallyIncrementCheckNumber || isBatchBill ? checkNumber : ''}
            control={control}
            error={!!errors?.[payment.id]?.details?.checkNumber}
            helperText={
              errors?.[payment.id]?.details?.checkNumber?.message ||
              (useAutoincrementedCheckNumber === false &&
                !isBatchBill &&
                'Automatic check numbering is turned off. When entering a check number manually, please ensure it is not already in use.')
            }
            disabled={useAutoincrementedCheckNumber || isBatchBill}
            InputProps={{
              inputComponent: InputFormat,
              inputProps: {
                suffix: useAutoincrementedCheckNumber && !isBatchBill ? ' (or next available number)' : '',
                inputType: 'number',
                thousandSeparator: false,
                allowLeadingZeros: true,
                allowNegative: false,
                decimalScale: 0,
              },
            }}
            sx={{ width: '50%' }}
          />
          {automaticallyIncrementCheckNumber && !isBatchBill ? (
            <Checkbox
              name={`${payment.id}.details.useAutoincrementedCheckNumber`}
              label="Use auto-incremented check number"
              defaultValue={automaticallyIncrementCheckNumber}
              control={control}
              onChange={event => {
                setValue(`${payment.id}.details.useAutoincrementedCheckNumber`, event.target.checked)
                if (event.target.checked) {
                  setValue(`${payment.id}.details.checkNumber`, checkNumber)
                  setTimeout(() => setValue(`${payment.id}.details.useAutoincrementedCheckNumber`, true), 0)
                }
              }}
            />
          ) : null}
        </Stack>
      </Stack>
      <Typography variant="h5" gutterBottom>
        Check Information
      </Typography>
      <Stack spacing={3}>
        <Stack direction="row" spacing={3}>
          <Controller
            name={`${payment.id}.information.payToTheOrder`}
            control={control}
            render={() => (
              <Stack direction="row" sx={{ width: '100%' }}>
                <Autocomplete
                  data-testid="pay-to-the-other-select"
                  size="small"
                  variant="outlined"
                  sx={{ width: '100%' }}
                  onChange={(_, contact) => updateContact(null, contact, payment.id)}
                  options={supplierContactOptions || []}
                  value={supplierContact}
                  getOptionLabel={option => `${option?.firstName} ${option?.lastName}` || option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.firstName === value?.firstName}
                  renderInput={params => <TextField {...params} label="Pay to the Order of" />}
                  defaultValue={
                    { firstName: customerData?.displayName, lastName: '' } ||
                    supplierContactOptions[0] ||
                    customerData?.contacts?.[0]
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id || 'CCA'} style={{ width: '100%' }}>
                      {`${option?.firstName} ${option?.lastName}`}
                    </li>
                  )}
                  groupBy={option => (option?.id ? 'Contacts' : 'Customer')}
                  renderGroup={params => (
                    <li>
                      <ListSubheader disableGutters sx={{ padding: '0 15px' }}>
                        {params?.group}
                      </ListSubheader>
                      {params?.children?.map(child => (
                        <MenuItem disableGutters sx={{ padding: 0, width: '100%' }}>
                          {child}
                        </MenuItem>
                      ))}
                    </li>
                  )}
                />
                <Button
                  startIcon={<AddCircleIcon />}
                  sx={{
                    minWidth: 'unset',
                    width: 40,
                    paddingRight: 0,
                    marginRight: -1,
                  }}
                  onClick={() =>
                    store.dispatch(
                      showFormModal({
                        formModalName: FORM_MODALS.NEW_CUSTOMER,
                        formModalProps: {
                          type: 'contacts',
                          customerId: customerData.id,
                        },
                      }),
                    )
                  }
                />
              </Stack>
            )}
          />
          <Controller
            name={`${payment.id}.information.location`}
            control={control}
            render={() => (
              <Autocomplete
                data-testid="location-select"
                variant="outlined"
                size="small"
                getOptionLabel={option => option?.locationName || option?.name || 'Contact Compliance Address'}
                isOptionEqualToValue={(option, value) =>
                  option?.name === value?.locationName || option?.name === value?.name
                }
                options={supplierLocationOptions || []}
                value={supplierLocation}
                defaultValue={customerData?.locations?.filter(location => locationIsNotEmpty(location))?.[0] || null}
                renderInput={params => <TextField {...params} label="Recipient Address" />}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id || 'CCA'} style={{ width: '100%' }}>
                    {option?.name || 'Contact Compliance Address'}
                  </li>
                )}
                sx={{ width: '100%' }}
                onChange={(_, location) => updateLocation(null, location, payment.id)}
              />
            )}
          />
        </Stack>
      </Stack>
      <Input
        data-testid="memo-input"
        name={`${payment.id}.information.memo`}
        label="Memo"
        size="small"
        variant="outlined"
        defaultValue={defaultMemo}
        control={control}
      />
    </Stack>
  )
}

PrintCheckBladeForm.propTypes = {
  payment: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  accountOptions: PropTypes.array.isRequired,
  defaultMemo: PropTypes.string,
  customerData: PropTypes.object,
  automaticallyIncrementCheckNumber: PropTypes.bool,
  checkNumber: PropTypes.string,
  supplierContactOptions: PropTypes.array,
  supplierLocationOptions: PropTypes.array,
  supplierContact: PropTypes.object,
  supplierLocation: PropTypes.object,
  updateContact: PropTypes.func,
  updateLocation: PropTypes.func,
  pricePrepend: PropTypes.string,
  isBatchBill: PropTypes.bool,
}

PrintCheckBladeForm.defaultProps = {
  defaultMemo: '',
  customerData: {},
  automaticallyIncrementCheckNumber: false,
  checkNumber: '',
  supplierContactOptions: [],
  supplierLocationOptions: [],
  supplierContact: {},
  supplierLocation: {},
  updateContact: () => {},
  updateLocation: () => {},
  pricePrepend: '$',
  isBatchBill: false,
}

export default PrintCheckBladeForm
